import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { registerUser } from '../services/userService';
import Footer from '../components/Footer';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const userTypeFromUrl = searchParams.get('type') || 'jobseeker';
  const referralCode = searchParams.get('ref') || '';
  // Lock role if referral code AND type provided (Franchise Link)
  const isRoleLocked = !!(referralCode && searchParams.get('type'));

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: userTypeFromUrl,
    phone: '',
    referredBy: referralCode
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    // First name validation
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (!formData.phone) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      errors.phone = 'Please enter a valid 10-digit phone number';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await registerUser({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        userType: formData.userType,
        phone: formData.phone,
        referredBy: formData.referredBy
      });

      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setError('An unexpected error occurred');
    }

    setLoading(false);
  };

  return (
    <div className="register-container">
      {/* Animated Gradient Background */}
      <div className="animated-bg">
        <div className="gradient-sphere sphere-1"></div>
        <div className="gradient-sphere sphere-2"></div>
        <div className="gradient-sphere sphere-3"></div>
        <div className="gradient-sphere sphere-4"></div>
      </div>

      <div className="content-wrapper">
        <div className="register-card-wrapper">
          {/* Header Section */}
          <div className="header-section">
            <div className="header-icon">
              <svg className="header-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={formData.userType === 'employer' ? "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" : formData.userType === 'agent' ? "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" : "M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"} />
              </svg>
            </div>
            <h1 className="welcome-title">
              {formData.userType === 'employer' ? 'Establish Employer Profile' : formData.userType === 'agent' ? 'Register Affiliate Account' : 'Create Candidate Credentials'}
            </h1>
            <p className="welcome-subtitle">
              {formData.userType === 'employer'
                ? 'Initiate recruitment capabilities and browse premium candidate pools.'
                : formData.userType === 'agent'
                  ? 'Access affiliate network logs and track referral performance.'
                  : 'Configure your talent profile to access elite career pathways.'
              }
            </p>
          </div>

          {/* Registration Form Card */}
          <div className="register-card">
            <form onSubmit={handleSubmit} className="register-form">
              
              {/* Name Fields */}
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Given Name (First Name)</label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="e.g., Jane"
                      className={`form-input ${validationErrors.firstName ? 'input-error' : ''}`}
                      required
                    />
                  </div>
                  {validationErrors.firstName && (
                    <p className="error-message">{validationErrors.firstName}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Family Name (Last Name)</label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="e.g., Doe"
                      className={`form-input ${validationErrors.lastName ? 'input-error' : ''}`}
                      required
                    />
                  </div>
                  {validationErrors.lastName && (
                    <p className="error-message">{validationErrors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Email Address */}
              <div className="form-group">
                <label className="form-label">Professional Email Address</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="e.g., jane.doe@company.com"
                    className={`form-input ${validationErrors.email ? 'input-error' : ''}`}
                    required
                  />
                </div>
                {validationErrors.email && (
                  <p className="error-message">{validationErrors.email}</p>
                )}
              </div>

              {/* Phone Number */}
              <div className="form-group">
                <label className="form-label">Contact Number (Mobile)</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="e.g., 5550199"
                    className={`form-input ${validationErrors.phone ? 'input-error' : ''}`}
                    required
                  />
                </div>
                {validationErrors.phone && (
                  <p className="error-message">{validationErrors.phone}</p>
                )}
              </div>

              {/* User Type Selection */}
              <div className="form-group">
                <label className="form-label">Pre-assigned Security Role</label>
                {isRoleLocked ? (
                  <div className="role-locked-display">
                    <span>
                      {formData.userType === 'jobseeker' ? 'Talent Candidate' : formData.userType === 'employer' ? 'Corporate Employer' : 'Network Affiliate'}
                    </span>
                    <span className="role-locked-badge">
                      Pre-selected via Link
                    </span>
                  </div>
                ) : (
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <select
                      id="userType"
                      name="userType"
                      value={formData.userType}
                      onChange={handleChange}
                      className="form-input form-select"
                    >
                      <option value="jobseeker">Talent Candidate - Seeking professional engagement</option>
                      <option value="employer">Corporate Employer - Deploying talent requisitions</option>
                      <option value="agent">Network Affiliate - Referrals & commissions</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Password Fields */}
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Secure Access Key</label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Specify secure key"
                      className={`form-input ${validationErrors.password ? 'input-error' : ''}`}
                      required
                    />
                  </div>
                  {validationErrors.password && (
                    <p className="error-message">{validationErrors.password}</p>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Verify Access Key</label>
                  <div className="input-wrapper">
                    <div className="input-icon">
                      <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Re-enter secure key"
                      className={`form-input ${validationErrors.confirmPassword ? 'input-error' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle"
                    >
                      {showPassword ? (
                        <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {validationErrors.confirmPassword && (
                    <p className="error-message">{validationErrors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="error-alert">
                  <svg className="alert-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>{error}</div>
                </div>
              )}

              {/* Submit Button */}
              <button type="submit" disabled={loading} className="submit-btn">
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    <span>Registering Account...</span>
                  </>
                ) : (
                  <>
                    <span>Initialize Security Profile</span>
                    <svg className="arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="divider">
                <div className="divider-line"></div>
                <span className="divider-text">Already have credentials?</span>
                <div className="divider-line"></div>
              </div>

              {/* Login Link */}
              <div className="login-link-container">
                <Link to="/login" className="login-link">
                  <span>Access Account / Authenticate</span>
                  <svg className="link-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="terms-section">
            <p>
              By initializing your profile, you accept our{' '}
              <Link to="/terms" className="legal-link">
                Platform Terms
              </Link>{' '}
              &{' '}
              <Link to="/privacy" className="legal-link">
                Privacy Regulations
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />

      <style jsx>{`
        /* Container Styles - Deep Blue Theme */
        .register-container {
          position: relative;
          min-height: 100vh;
          background: linear-gradient(135deg, #0e3a61 0%, #051728 100%);
          overflow-x: hidden;
        }

        /* Animated Gradient Background */
        .animated-bg {
          position: absolute;
          inset: 0;
          overflow: hidden;
          z-index: 1;
        }

        .gradient-sphere {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.4;
          animation: float 20s ease-in-out infinite;
        }

        .sphere-1 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.02));
          top: -150px;
          left: -150px;
          animation-delay: 0s;
        }

        .sphere-2 {
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(226, 130, 47, 0.1), rgba(226, 130, 47, 0.01));
          bottom: -200px;
          right: -200px;
          animation-delay: -5s;
        }

        .sphere-3 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(14, 58, 97, 0.3), rgba(14, 58, 97, 0.05));
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          animation-delay: -10s;
        }

        .sphere-4 {
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.02));
          top: 20%;
          right: 10%;
          animation-delay: -7s;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.05);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.98);
          }
        }

        /* Content Wrapper */
        .content-wrapper {
          position: relative;
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          padding: 3rem 2rem;
        }

        .register-card-wrapper {
          width: 100%;
          max-width: 580px;
        }

        /* Header Section */
        .header-section {
          text-align: center;
          margin-bottom: 2rem;
          animation: fadeInUp 0.6s ease-out;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .header-icon {
          position: relative;
          width: 80px;
          height: 80px;
          margin: 0 auto 1.25rem;
          background: linear-gradient(135deg, #0e3a61 0%, #1a5c96 100%);
          border-radius: 1.25rem 2rem 1.25rem 2rem;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 20px 40px -12px rgba(14, 58, 97, 0.4);
          transition: transform 0.3s ease;
        }

        .header-icon:hover {
          transform: scale(1.05);
        }

        .header-icon-svg {
          width: 36px;
          height: 36px;
          color: white;
        }

        .welcome-title {
          font-size: 1.875rem;
          font-weight: 700;
          background: linear-gradient(135deg, #ffffff 0%, #dbeafe 100%);
          background-clip: text;
          -webkit-background-clip: text;
          color: transparent;
          margin-bottom: 0.5rem;
          letter-spacing: -0.02em;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .welcome-subtitle {
          color: rgba(255, 255, 255, 0.95);
          font-size: 0.95rem;
          font-weight: 400;
          max-width: 32rem;
          margin: 0 auto;
          line-height: 1.7;
        }

        /* Register Card - Premium Asymmetric Geometry */
        .register-card {
          position: relative;
          background: #ffffff;
          border-radius: 2rem 3.5rem 2rem 3.5rem;
          padding: 2.5rem;
          box-shadow: 0 35px 80px -30px rgba(14, 58, 97, 0.3);
          animation: fadeInUp 0.6s ease-out 0.1s both;
          overflow: hidden;
          border: 1px solid rgba(14, 58, 97, 0.08);
        }

        .register-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        /* Grid */
        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.25rem;
        }

        @media (max-width: 640px) {
          .form-grid {
            grid-template-columns: 1fr;
            gap: 1.25rem;
          }
        }

        /* Form Elements */
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #4a5568;
          letter-spacing: -0.01em;
        }

        .input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #a0aec0;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          pointer-events: none;
        }

        .form-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.75rem;
          border-radius: 1rem;
          border: 1.5px solid #e2e8f0;
          font-size: 0.875rem;
          transition: all 0.2s ease;
          background: #ffffff;
          color: #2d3748;
          font-weight: 450;
        }

        .form-input:focus {
          outline: none;
          border-color: #0e3a61;
          box-shadow: 0 0 0 4px rgba(14, 58, 97, 0.10);
        }

        .form-input::placeholder {
          color: #cbd5e1;
          font-weight: 400;
        }

        .form-select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23a0aec0'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          background-size: 1rem;
          padding-right: 2.5rem;
        }

        .role-locked-display {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          border: 1.5px solid #e2e8f0;
          background: #f7fafc;
          border-radius: 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: #2d3748;
        }

        .role-locked-badge {
          font-size: 0.75rem;
          font-weight: 700;
          background: rgba(14, 58, 97, 0.08);
          color: #0e3a61;
          padding: 0.25rem 0.625rem;
          border-radius: 9999px;
          border: 1px solid rgba(14, 58, 97, 0.12);
        }

        .input-error {
          border-color: #f56565;
        }

        .input-error:focus {
          border-color: #f56565;
          box-shadow: 0 0 0 4px rgba(245, 101, 101, 0.1);
        }

        .password-toggle {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #a0aec0;
          padding: 0;
          display: flex;
          transition: color 0.2s;
        }

        .password-toggle:hover {
          color: #0e3a61;
        }

        .icon-svg {
          width: 1.125rem;
          height: 1.125rem;
        }

        .error-message {
          color: #f56565;
          font-size: 0.7rem;
          margin-top: 0.25rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        /* Alerts */
        .error-alert {
          background: #fff5f5;
          border: 1px solid #fed7d7;
          border-radius: 1rem;
          padding: 0.75rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #e53e3e;
          font-size: 0.813rem;
          font-weight: 500;
        }

        .alert-icon {
          width: 1.125rem;
          height: 1.125rem;
          flex-shrink: 0;
        }

        /* Submit Button */
        .submit-btn {
          width: 100%;
          padding: 0.875rem;
          border-radius: 1rem;
          background: linear-gradient(135deg, #0e3a61 0%, #1a5c96 100%);
          color: white;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.625rem;
          margin-top: 0.5rem;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 15px 25px -10px rgba(14, 58, 97, 0.4);
        }

        .submit-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .submit-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .spinner {
          width: 1rem;
          height: 1rem;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .arrow-icon {
          width: 1rem;
          height: 1rem;
          transition: transform 0.2s;
        }

        .submit-btn:hover .arrow-icon {
          transform: translateX(4px);
        }

        /* Divider */
        .divider {
          display: flex;
          align-items: center;
          margin: 1.5rem 0;
        }

        .divider-line {
          flex: 1;
          height: 1px;
          background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
        }

        .divider-text {
          padding: 0 1rem;
          font-size: 0.7rem;
          color: #a0aec0;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Login Link Section */
        .login-link-container {
          display: flex;
          justify-content: center;
          width: 100%;
        }

        .login-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #0e3a61;
          font-weight: 700;
          font-size: 0.95rem;
          transition: all 0.2s;
          text-decoration: none;
        }

        .login-link:hover {
          color: #e2822f;
        }

        .link-arrow {
          width: 1rem;
          height: 1rem;
          transition: transform 0.2s;
        }

        .login-link:hover .link-arrow {
          transform: translateX(4px);
        }

        /* Terms Section */
        .terms-section {
          margin-top: 1.5rem;
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.9);
          text-align: center;
          line-height: 1.5;
        }

        .terms-section p {
          margin: 0;
        }

        .legal-link {
          color: rgba(255, 255, 255, 1);
          font-weight: 600;
          text-decoration: underline;
          transition: color 0.2s;
        }

        .legal-link:hover {
          color: #e2822f;
        }

        /* Responsive adjustments */
        @media (max-width: 640px) {
          .content-wrapper {
            padding: 1.5rem 1rem;
          }
          
          .register-card {
            padding: 1.5rem;
            border-radius: 1.5rem;
          }
          
          .welcome-title {
            font-size: 1.5rem;
          }
          
          .header-icon {
            width: 64px;
            height: 64px;
          }

          .header-icon-svg {
            width: 28px;
            height: 28px;
          }
        }
      `}</style>
    </div>
  );
};

export default Register;
